import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorContext {
  user?: {
    id?: string;
    email?: string;
  };
  extra?: Record<string, any>;
  tags?: Record<string, string>;
  level?: 'error' | 'warning' | 'info' | 'debug';
}

export function useErrorTracking() {
  const captureError = useCallback((
    error: Error,
    context: ErrorContext = {}
  ) => {
    const { user, extra, tags, level = 'error' } = context;

    Sentry.withScope((scope) => {
      // Set user context
      if (user) {
        scope.setUser(user);
      }

      // Set additional context
      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      // Set tags
      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // Set level
      scope.setLevel(level);

      // Capture the error
      Sentry.captureException(error);
    });
  }, []);

  const captureMessage = useCallback((
    message: string,
    context: ErrorContext = {}
  ) => {
    const { user, extra, tags, level = 'info' } = context;

    Sentry.withScope((scope) => {
      if (user) {
        scope.setUser(user);
      }

      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  }, []);

  const addBreadcrumb = useCallback((
    message: string,
    category?: string,
    data?: Record<string, any>
  ) => {
    Sentry.addBreadcrumb({
      message,
      category: category || 'custom',
      data,
      timestamp: Date.now() / 1000,
    });
  }, []);

  const setUserContext = useCallback((user: {
    id?: string;
    email?: string;
    username?: string;
  }) => {
    Sentry.setUser(user);
  }, []);

  const clearUserContext = useCallback(() => {
    Sentry.setUser(null);
  }, []);

  return {
    captureError,
    captureMessage,
    addBreadcrumb,
    setUserContext,
    clearUserContext
  };
}